import type { ApiSchemas } from '@rental/contracts/client'
import { format, formatDate, formatDateTime, formatEuro, messagesFor } from '@rental/i18n'
import type { ReactNode } from 'react'
import { TermsBody } from '../components/TermsText'
import { Facts } from '../components/ui'

type Document = ApiSchemas['ContractDocument']

/** The contract's own words are Italian for every customer (Fady, 8 Oct 2026; Q-26). */
const LOCALE = 'it'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-t border-border pt-4">
      <h2 className="text-xl">{title}</h2>
      {children}
    </section>
  )
}

/**
 * CTR-1 on the customer's screen: the frozen contract, as the PDF prints it.
 * Everything here comes from the document the server froze and hashed; the
 * screen formats it and adds nothing.
 */
export function ContractView({ document: d }: { document: Document }) {
  const m = messagesFor(LOCALE).contract
  const or = (value: string | null) => value ?? m.notConfigured
  const euro = (cents: number) => formatEuro(cents, LOCALE)
  const optional = (label: string, value: string | null): (readonly [string, string])[] =>
    value ? [[label, value]] : []
  return (
    <article lang="it" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <p className="text-2xl font-bold">{m.title}</p>
        <p className="text-text-muted">{format(m.booking, { code: d.booking.code })}</p>
      </header>

      <Section title={m.sections.company}>
        <Facts
          items={[
            [m.fields.name, or(d.company.name)],
            [m.fields.address, or(d.company.address)],
            [m.fields.vatNumber, or(d.company.vatNumber)],
          ]}
        />
      </Section>

      <Section title={m.sections.customer}>
        <Facts
          items={[
            [m.fields.name, `${d.customer.firstName} ${d.customer.lastName}`],
            ...optional(
              m.fields.birthDate,
              d.customer.birthDate ? formatDate(LOCALE, d.customer.birthDate) : null,
            ),
            ...optional(m.fields.birthPlace, d.customer.birthPlace),
            ...optional(m.fields.nationality, d.customer.nationality),
            ...optional(m.fields.address, d.customer.address),
            ...optional(m.fields.email, d.customer.email),
            ...optional(m.fields.phone, d.customer.phone),
          ]}
        />
      </Section>

      <Section title={m.sections.licence}>
        {d.licence ? (
          <Facts
            items={[
              [m.fields.licenceNumber, d.licence.number],
              [m.fields.issuingCountry, d.licence.issuingCountry],
              ...optional(
                m.fields.expiresOn,
                d.licence.expiresOn ? formatDate(LOCALE, d.licence.expiresOn) : null,
              ),
              [m.fields.categories, d.licence.categories.join(', ')],
            ]}
          />
        ) : (
          <p>{m.noLicence}</p>
        )}
      </Section>

      {d.secondDriver ? (
        <Section title={m.sections.secondDriver}>
          <Facts
            items={[
              [m.fields.name, `${d.secondDriver.firstName} ${d.secondDriver.lastName}`],
              ...optional(
                m.fields.birthDate,
                d.secondDriver.birthDate ? formatDate(LOCALE, d.secondDriver.birthDate) : null,
              ),
              ...optional(m.fields.birthPlace, d.secondDriver.birthPlace),
              ...optional(m.fields.nationality, d.secondDriver.nationality),
              ...(d.secondDriver.licence
                ? ([
                    [m.fields.licenceNumber, d.secondDriver.licence.number],
                    [m.fields.issuingCountry, d.secondDriver.licence.issuingCountry],
                    ...optional(
                      m.fields.expiresOn,
                      d.secondDriver.licence.expiresOn
                        ? formatDate(LOCALE, d.secondDriver.licence.expiresOn)
                        : null,
                    ),
                    [m.fields.categories, d.secondDriver.licence.categories.join(', ')],
                  ] as const)
                : [[m.sections.licence, m.noLicence] as const]),
            ]}
          />
        </Section>
      ) : null}

      <Section title={m.sections.vehicle}>
        <Facts
          items={[
            [m.fields.plate, d.vehicle.plate],
            [m.fields.model, `${d.vehicle.make} ${d.vehicle.model}`],
            [m.fields.category, d.vehicle.category],
          ]}
        />
      </Section>

      <Section title={m.sections.period}>
        <Facts
          items={[
            [m.fields.from, `${formatDateTime(LOCALE, d.period.startsAt)} · ${d.period.pickup}`],
            [m.fields.to, `${formatDateTime(LOCALE, d.period.endsAt)} · ${d.period.return}`],
          ]}
        />
      </Section>

      <Section title={m.sections.prices}>
        <table className="w-full">
          <tbody>
            {d.prices.lines.map((line, index) => (
              // The frozen order is the lines' identity.
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed list
              <tr key={index}>
                {/* The line's name is its row's header, as on the booking page (UI-2g). */}
                <th scope="row" className="py-1 pe-4 text-start font-regular">
                  {line.label}
                </th>
                <td className="figures py-1 text-end">{euro(line.totalCents)}</td>
              </tr>
            ))}
            <tr className="border-t border-border font-bold">
              <th scope="row" className="py-1 pe-4 text-start">
                {m.fields.total}
              </th>
              <td className="figures py-1 text-end">{euro(d.prices.totalCents)}</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section title={m.sections.cover}>
        <p>
          {d.cover
            ? `${d.cover.name} · ${m.fields.excess}: ${
                d.cover.excessCents === null ? m.noExcess : euro(d.cover.excessCents)
              }`
            : m.noCover}
        </p>
      </Section>

      <Section title={m.sections.deposit}>
        <p>
          {d.deposit.amountCents === null
            ? m.depositPending
            : format(m.depositAmount, { amount: euro(d.deposit.amountCents) })}
        </p>
      </Section>

      {d.fees.length > 0 ? (
        <Section title={m.sections.fees}>
          <ul className="flex flex-col gap-1">
            {d.fees.map((fee) => (
              <li key={fee.label} className="flex justify-between gap-4">
                <span>{fee.label}</span>
                <span className="figures">{euro(fee.amountCents)}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title={d.terms.title}>
        <TermsBody body={d.terms.body} />
        {/* S9: the fingerprint's 64 characters have nowhere to break: on a phone they wrap. */}
        <p className="text-sm text-text-muted wrap-anywhere">
          {format(m.termsFingerprint, { version: d.terms.version, hash: d.terms.sha256 })}
        </p>
      </Section>
    </article>
  )
}
